/**
 * 令牌 → CSS 变量。
 *
 * 为什么走 CSS 变量而不是把色值当 props 传:组件的颜色要用在 `:hover`、`:focus`、
 * `::placeholder`、媒体查询这些**伪类与伪元素**里,而那些地方读不到 props ——
 * 只有 CSS 变量能在不写内联样式的前提下被它们读到。
 * 于是组件根元素上按 tone 绑一组 `--sd-*`, `<style scoped>` 里只写 `var(--sd-*)`。
 *
 * ═══ 视图怎么用 ═══
 *
 * AppShell / LoginLayout 把这一整套变量挂在根元素上,**CSS 变量会继承** ——
 * 所以视图在自己的 `<style scoped>` 里直接写 `color: var(--sd-text2)` 就能拿到
 * 当前端的颜色,不必再 import 任何东西,也不必写裸色值(规格 §3 禁止)。
 *
 * 另外这套变量还会同步到 `documentElement` 上,原因见下面 useToneVars 的注释。
 */
import { computed, onUnmounted, watchEffect, type ComputedRef } from 'vue'
import { TOKENS, isDarkTone, type Tone } from './tokens'

export type ToneVars = Record<string, string>

/**
 * 一组令牌变量。浅色端的侧边栏仍是深色(规格 §2),所以它不从 light 里取文字色。
 *
 * 三档的取法(S-41 加了 ops-dark 之后):
 *   light     —— 内容区取 light.*,侧栏取 light.sidebar*(深色渐变)
 *   dark      —— 监管端:内容区与侧栏都取 dark.*,侧栏比内容区再深一档(bgDeep)
 *   ops-dark  —— 运营端:内容区取 dark 家族(画板那 9 个值与它逐值相等),侧栏取
 *                light.sidebar*(画板侧栏渐变与它逐值一致),登录页品牌区同侧栏
 */
export function toneVars(tone: Tone): ToneVars {
  const dark = isDarkTone(tone)
  const alpha = dark ? TOKENS.semantic.alpha.dark : TOKENS.semantic.alpha.light
  const semantic = dark ? TOKENS.semantic.dark : TOKENS.semantic.light

  const base: ToneVars = {
    '--sd-brand': dark ? TOKENS.brand[500] : TOKENS.brand[600],
    '--sd-brand-hover': dark ? TOKENS.brand[400] : TOKENS.brand[500],
    '--sd-brand-pressed': dark ? TOKENS.brand[900] : TOKENS.brand[700],
    '--sd-brand-soft': alpha.brandBg,
    '--sd-brand-border': alpha.brandBorder,
    // 比例条的渐隐端(§4.3 的 BarList:brand.500 → brand.300)
    '--sd-brand-lite': TOKENS.brand[300],
    // 品牌方块(侧栏的品牌标 / 登录页品牌区那只方块)的底:两块画板都用 brand-400(#6697F8)
    '--sd-brand-mark': TOKENS.brand[400],

    '--sd-success': semantic.success,
    '--sd-warning': semantic.warning,
    '--sd-danger': semantic.danger,
    '--sd-info': semantic.info,
    '--sd-success-bg': alpha.successBg,
    '--sd-success-border': alpha.successBorder,
    '--sd-warning-bg': alpha.warningBg,
    '--sd-warning-border': alpha.warningBorder,
    '--sd-danger-bg': alpha.dangerBg,
    '--sd-danger-border': alpha.dangerBorder,
    '--sd-info-bg': alpha.infoBg,
    '--sd-info-border': alpha.infoBorder,

    '--sd-radius-sm': TOKENS.radius.sm,
    '--sd-radius-md': TOKENS.radius.md,
    '--sd-radius-lg': TOKENS.radius.lg,
    '--sd-radius-xl': TOKENS.radius.xl,
    '--sd-radius-pill': TOKENS.radius.pill,

    '--sd-font-sans': TOKENS.font.sans,
    '--sd-font-mono': TOKENS.font.mono,
  }

  if (tone === 'ops-dark') {
    return {
      ...base,
      // 内容区 = dark 家族。画板的 9 个 ops-dark 变量与它逐值相等(2026-10-16 核对),
      // 不复制一组同值令牌 —— 理由写在 tokens.ts 的 TOKENS.dark 那段注释里
      '--sd-bg': TOKENS.dark.bgBase,
      '--sd-bg-deep': TOKENS.dark.bgDeep,
      '--sd-surface': TOKENS.dark.panel,
      '--sd-surface-alt': TOKENS.dark.panelAlt,
      '--sd-border': TOKENS.dark.border,
      '--sd-border-strong': TOKENS.dark.borderStrong,
      '--sd-text1': TOKENS.dark.text1,
      '--sd-text2': TOKENS.dark.text2,
      '--sd-text3': TOKENS.dark.text3,
      // 强调色**不继承监管端那一支**:#35D0F0 青色是监管大屏的专属(规格 §5.1),
      // 运营端画板上的高亮一律 $brand-blue-400。全仓目前没有组件消费 --sd-accent
      // (读它的是将来带图表/大屏的件),这一档存在的意义就是「两个深色端在这一格上分开」
      '--sd-accent': TOKENS.brand[400],
      // 侧栏三件取 light.sidebar* —— 画板侧栏渐变 #0C1728→#101E33 与 light.sidebarBg
      // 逐值一致、激活底 #3D77F229 与 light.sidebarActiveBg 一致、文字 #93A4BC 与 sidebarText 一致
      '--sd-sidebar-bg': TOKENS.light.sidebarBg,
      '--sd-sidebar-border': TOKENS.light.sidebarBorder,
      '--sd-sidebar-text': TOKENS.light.sidebarText,
      '--sd-sidebar-text-active': TOKENS.light.sidebarTextActive,
      '--sd-sidebar-active-bg': TOKENS.light.sidebarActiveBg,
      '--sd-sidebar-active-bar': TOKENS.light.sidebarActiveBar,
      // 登录页左栏 = O8 品牌区那条渐变(与侧栏是同一块底)
      '--sd-brand-panel': TOKENS.light.sidebarBg,
      // 投影取 dark 档:深底上浅色投影不可见,那两档本来就是为深底写的
      '--sd-shadow-sm': TOKENS.shadow.dark.sm,
      '--sd-shadow-md': TOKENS.shadow.dark.sm,
      '--sd-shadow-lg': TOKENS.shadow.dark.glow,
      '--sd-shadow-glow': TOKENS.shadow.dark.glow,
    }
  }

  if (tone === 'dark') {
    return {
      ...base,
      '--sd-bg': TOKENS.dark.bgBase,
      '--sd-bg-deep': TOKENS.dark.bgDeep,
      '--sd-surface': TOKENS.dark.panel,
      '--sd-surface-alt': TOKENS.dark.panelAlt,
      '--sd-border': TOKENS.dark.border,
      '--sd-border-strong': TOKENS.dark.borderStrong,
      '--sd-text1': TOKENS.dark.text1,
      '--sd-text2': TOKENS.dark.text2,
      '--sd-text3': TOKENS.dark.text3,
      '--sd-accent': TOKENS.dark.accent,
      // 深色端的侧边栏与内容区同为深色,但再深一档 —— 两块深色贴在一起时需要一条边界
      '--sd-sidebar-bg': TOKENS.dark.bgDeep,
      '--sd-sidebar-border': TOKENS.dark.border,
      '--sd-sidebar-text': TOKENS.dark.text2,
      '--sd-sidebar-text-active': TOKENS.dark.text1,
      '--sd-sidebar-active-bg': TOKENS.dark.glow,
      '--sd-sidebar-active-bar': TOKENS.brand[500],
      // 登录页左栏:深色端的底也要有渐变,不然两块深色会糊成一片
      '--sd-brand-panel': `linear-gradient(180deg, ${TOKENS.dark.bgDeep} 0%, ${TOKENS.dark.bgBase} 100%)`,
      '--sd-shadow-sm': TOKENS.shadow.dark.sm,
      '--sd-shadow-md': TOKENS.shadow.dark.sm,
      '--sd-shadow-lg': TOKENS.shadow.dark.glow,
      '--sd-shadow-glow': TOKENS.shadow.dark.glow,
    }
  }

  return {
    ...base,
    '--sd-bg': TOKENS.light.appBg,
    '--sd-bg-deep': TOKENS.light.navDeep,
    '--sd-surface': TOKENS.light.surface,
    '--sd-surface-alt': TOKENS.light.surfaceAlt,
    '--sd-border': TOKENS.light.border,
    '--sd-border-strong': TOKENS.light.borderStrong,
    '--sd-text1': TOKENS.light.text1,
    '--sd-text2': TOKENS.light.text2,
    '--sd-text3': TOKENS.light.text3,
    '--sd-accent': TOKENS.brand[600],
    '--sd-sidebar-bg': TOKENS.light.sidebarBg,
    '--sd-sidebar-border': TOKENS.light.sidebarBorder,
    '--sd-sidebar-text': TOKENS.light.sidebarText,
    '--sd-sidebar-text-active': TOKENS.light.sidebarTextActive,
    '--sd-sidebar-active-bg': TOKENS.light.sidebarActiveBg,
    '--sd-sidebar-active-bar': TOKENS.light.sidebarActiveBar,
    // 登录页左栏两端都是深色版画,浅色端也照样用那条深色渐变(规格 §3.2 的 sidebarBg)
    '--sd-brand-panel': TOKENS.light.sidebarBg,
    '--sd-shadow-sm': TOKENS.shadow.light.sm,
    '--sd-shadow-md': TOKENS.shadow.light.md,
    '--sd-shadow-lg': TOKENS.shadow.light.lg,
    // 浅色端没有「辉光」这个概念,`glow` 落到这里表示「比默认更抬高一档」
    '--sd-shadow-glow': TOKENS.shadow.light.md,
  }
}

/**
 * 当前这一组变量挂在哪一个组件实例上。
 *
 * 为什么要有它:两个端的 App.vue 是 `v-if / v-else` 两个分支(壳 vs 登录页),
 * 切换时一个新实例挂载、另一个卸载。若卸载的那个无条件清掉根元素上的变量,
 * 它会**把新实例刚挂上去的那套一起清掉** —— 表现是切到登录页之后整个页面失去配色,
 * 而这在开发机上十次里可能只出现两三次(取决于挂载与卸载的先后)。
 */
let owner: symbol | null = null

/** 变量名集合。清理时要按名字逐个删,不然会把别的库挂在 :root 上的变量一并抹掉 */
const OWNED_KEYS = Object.keys(toneVars('light'))

function applyToDocument(vars: ToneVars, tone: Tone): void {
  const root = document.documentElement
  for (const [key, value] of Object.entries(vars)) root.style.setProperty(key, value)

  // 文档本身的底也刷成内容区的底色。
  //
  // 壳子(`.shell`)已经是 `min-height: 100vh`,正常情况下盖得住视口 ——
  // 但「正常情况下盖得住」不是一条能靠眼睛维持的性质:哪天某个视图在
  // 壳子外面塞了一个元素、或者浏览器弹了一下回弹效果,露出来的就是
  // 浏览器默认的白底。深色端上那一块白会刺眼到没法解释。
  // 把文档底一并刷掉之后,「露底」这件事在结构上就不存在了。
  root.style.backgroundColor = isDarkTone(tone) ? TOKENS.dark.bgBase : TOKENS.light.appBg

  // 原生控件(滚动条、日期选择器的弹层、autofill)按明暗走 ——
  // 不写这一行,深色端会挂一条亮色滚动条,而它恰恰在屏幕最右边一条上。
  // `colorScheme` 只认 light/dark 两个字面值,第三档(ops-dark)映射到 dark
  root.style.colorScheme = isDarkTone(tone) ? 'dark' : 'light'
}

function clearFromDocument(): void {
  const root = document.documentElement
  for (const key of OWNED_KEYS) root.style.removeProperty(key)
  root.style.backgroundColor = ''
  root.style.colorScheme = ''
}

/**
 * 组件用这个:返回绑定到根元素的变量,并顺手同步一份到 `documentElement`。
 *
 * 同步到根元素是必须的,不是保险:**naive-ui 的抽屉、弹窗、气泡默认 teleport 到 body**,
 * 挂载点搬到了组件根元素之外,继承链在那里断掉 ——
 * 于是浮层里的 `var(--sd-text2)` 解析不出来,颜色静默变回继承值(通常是纯黑)。
 * 那种失效只在打开抽屉时才出现,页面上别的地方全是好的。
 */
export function useToneVars(tone: () => Tone): ComputedRef<ToneVars> {
  const vars = computed(() => toneVars(tone()))
  const mine = Symbol('sd-tone-vars')
  owner = mine
  watchEffect(() => applyToDocument(vars.value, tone()))
  onUnmounted(() => {
    if (owner !== mine) return
    owner = null
    clearFromDocument()
  })
  return vars
}
